import { useScopedAtomValue } from '@lemon8/web-app-shared/ssr';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { ImmersivePostItemAtoms } from '@discover/body/immersive-post/immersive-post-item/atoms';
import { useTranslation } from 'react-i18next';
import useReflowClick from '~/utils/use-reflow-click';
import { ClickPosition } from '~/utils/click-position';
import { Link, useNavigate } from '@remix-run/react';
import { ArticleDetailUtil } from 'shared/models';
import { useActiveToArticle } from '~/utils/active/use-active-to-article';
import { useViewTracking } from '~/utils/use-view-tracking';
import { useAtomValue } from 'jotai';
import { $IsMobileUser } from '~/utils/is-mobile-user';
import { removeEscapedSpecialXmlCharacter } from 'shared/utils/escape-xml';
import { $Locale } from '@lemon8/web-app-shared/atom';
import { useSEOHref } from '~/utils/build-seo-url';

import './style.scss';

export const ArticleBody: FC = () => {
  const { t } = useTranslation();
  const { region } = useAtomValue($Locale);
  const article = useScopedAtomValue(ImmersivePostItemAtoms.$CurrentArticle);
  const activeToArticle = useActiveToArticle(article);
  const isMobileUser = useAtomValue($IsMobileUser);
  const [showEllipse, setShowEllipse] = useState(false);
  const shortContentRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const articleHref = useSEOHref(ArticleDetailUtil.getArticlePath(article));

  const seeMoreObservedRef = useViewTracking({
    onView: useCallback(() => {
      if (isMobileUser) {
        window.Tea('wap_cta_show', {
          current_position: ClickPosition.main_read_more,
          group_id: article.groupId,
        });
      }
    }, []),
  });

  const handleSeeMoreClick = useReflowClick(
    {
      clickEvent: () => ({
        current_position: ClickPosition.main_read_more,
      }),
      defaultBehavior: () => {
        if (region === article.articleRegion) {
          navigate(articleHref);
        } else {
          window.open(articleHref, '_blank');
        }
      },
      reflowBehavior: () => {
        activeToArticle(ClickPosition.main_read_more);
      },
    },
    [activeToArticle],
  );

  useEffect(() => {
    const shortContent = shortContentRef.current;
    if (!shortContent) {
      return;
    }

    const { scrollHeight, clientHeight } = shortContent;

    if (scrollHeight > clientHeight + 10) {
      setShowEllipse(true);
    }
  }, []);

  const shortContent = removeEscapedSpecialXmlCharacter(article.shortContent.replace(/\s+/g, ' '));

  return (
    <div className="article-body" ref={seeMoreObservedRef} onClick={handleSeeMoreClick}>
      <h2 className="title">{article.title}</h2>
      {shortContent && (
        <div className="short-content-wrapper">
          <p className="short-content" ref={shortContentRef}>
            <object className="read-more">
              {showEllipse && '... '}
              <Link to={articleHref} onClick={(e) => e.preventDefault()}>
                {t('read_more')}
              </Link>
            </object>
            {shortContent}
          </p>
        </div>
      )}
    </div>
  );
};
